import { Node } from "@tiptap/core";
import { Table, TableRow } from "@tiptap/extension-table";
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import type { DOMOutputSpec } from '@tiptap/pm/model';
import { narrowTableMode, responsiveTableWrapper } from '../../general/table-responsive';

export const TableWithAttributes = Table.extend({
  name: 'table',
  content: 'table_row+',
  addAttributes() {
    return {
      ...this.parent?.(),
      style: { default: null, parseHTML: el => el.getAttribute('style') },
      tableWidth: { default: null, parseHTML: el => el.getAttribute('data-width') || el.style.getPropertyValue('--table-width').trim() || el.style.width || null },
      tableAlign: { default: null, parseHTML: el => el.getAttribute('data-align') || null },
      cellPadding: { default: null, parseHTML: el => el.getAttribute('data-cell-padding') || el.style.getPropertyValue('--table-cell-padding').trim() || null },
      cellSpacing: { default: null, parseHTML: el => el.getAttribute('data-cell-spacing') || el.style.getPropertyValue('--table-cell-spacing').trim() || el.style.borderSpacing || null },
      borderWidth: { default: null, parseHTML: el => el.getAttribute('data-border-width') || el.style.getPropertyValue('--table-border-width').trim() || el.style.borderWidth || null },
      borderStyle: { default: null, parseHTML: el => el.getAttribute('data-border-style') || el.style.getPropertyValue('--table-border-style').trim() || el.style.borderStyle || null },
      stylePreset: { default: null, parseHTML: el => el.getAttribute('data-style-preset') || ['default', 'primary', 'info', 'success', 'warning', 'danger', 'muted'].find(preset => el.classList.contains(`table-${preset}`)) || null },
      borderCollapse: { default: null, parseHTML: el => el.getAttribute('data-border-collapse') || el.style.borderCollapse || (el.classList.contains('table-collapse') ? 'collapse' : null) },
      stripedRows: { default: false, parseHTML: el => el.getAttribute('data-striped') === 'true' },
      caption: { default: '', parseHTML: el => el.getAttribute('data-caption') || '' },
      narrowScreen: { default: null, parseHTML: el => narrowTableMode(el.getAttribute('data-narrow-screen')) },
    }
  },
  renderHTML({ HTMLAttributes }) {
    const { 
        style,
        tableWidth, 
        tableAlign, 
        cellPadding, 
        cellSpacing, 
        borderWidth, 
        borderStyle, 
        stylePreset, 
        borderCollapse, 
        stripedRows, 
        caption,
        narrowScreen,
        ...rest 
    } = HTMLAttributes;

    // 1. Build Style String (Safely filtering out "null" strings)
    const stylesArray = [
        // Regenerate managed variables so reloading/editing cannot accumulate stale values.
        style?.split(';').filter((declaration: string) =>
            !/^\s*--table-(width|border-width|border-style|cell-spacing|cell-padding)\s*:/.test(declaration)
        ).map((declaration: string) => declaration.trim()).filter(Boolean).join('; ') || null,
        (tableWidth && String(tableWidth) !== 'null') ? `--table-width: ${tableWidth}` : null,
        (borderWidth && String(borderWidth) !== 'null') ? `--table-border-width: ${borderWidth}` : null,
        (borderStyle && String(borderStyle) !== 'null') ? `--table-border-style: ${borderStyle}` : null,
        (cellSpacing && String(cellSpacing) !== 'null') ? `--table-cell-spacing: ${cellSpacing}` : null,
        (cellPadding && String(cellPadding) !== 'null') ? `--table-cell-padding: ${cellPadding}` : null,
    ].filter(val => val && !String(val).includes(': null'));

    const finalStyleString = stylesArray.join('; ') || undefined;

    // 2. Build Class String (Handling null presets/aligns)
    const classes = [
        'table-rendered',
        (stylePreset && String(stylePreset) !== 'null') ? `table-${stylePreset}` : '',
        (tableAlign && String(tableAlign) !== 'null') ? `table-align-${tableAlign}` : '',
        stripedRows ? 'table-striped' : '',
        caption ? 'has-custom-caption' : '',
        borderCollapse === 'collapse' ? 'table-collapse' : ''
    ].filter(Boolean).join(' ');

    // 3. Return the full structure
    const table: DOMOutputSpec = ['table', {
        ...rest, 
        style: finalStyleString, 
        class: classes, 
        'data-caption': caption || undefined,
        'data-width': (tableWidth && String(tableWidth) !== 'null') ? tableWidth : undefined,
        'data-align': (tableAlign && String(tableAlign) !== 'null') ? tableAlign : undefined,
        'data-striped': stripedRows ? 'true' : 'false',
        'data-cell-padding': cellPadding || undefined,
        'data-cell-spacing': cellSpacing || undefined,
        'data-border-width': borderWidth || undefined,
        'data-border-style': borderStyle || undefined,
        'data-style-preset': stylePreset || undefined,
        'data-border-collapse': borderCollapse || undefined,
        'data-narrow-screen': narrowTableMode(narrowScreen) || undefined,
    }, ['tbody', 0]];
    const mode = narrowTableMode(narrowScreen);
    return mode ? ['div', responsiveTableWrapper(mode, caption), table] : table;
    }
});

export const CustomTableRow = TableRow.extend({
  name: 'table_row',
  content: '(table_cell | table_header)+',
  addAttributes() {
    return {
      ...this.parent?.(),
      style: { default: null, parseHTML: el => el.getAttribute('style') },
      rowHeight: { default: null, parseHTML: el => el.style.height || el.getAttribute('data-row-height') || null },
      rowStyle: { default: null, parseHTML: el => el.getAttribute('data-row-style') || null },
      hAlign: { default: null, parseHTML: el => el.style.textAlign || el.getAttribute('data-text-align') || null },
      vAlign: { default: null, parseHTML: el => el.style.verticalAlign || el.getAttribute('data-v-align') || null },
    }
  },
  renderHTML({ HTMLAttributes }) {
    const { style, rowHeight, rowStyle, hAlign, vAlign, ...rest } = HTMLAttributes
    
    // Add hAlign (text-align) and vAlign (vertical-align) to the style array to ensure browser rendering
    const styles = [
      style, 
      (rowHeight && rowHeight !== 'auto') ? `height: ${rowHeight}` : '',
      (hAlign && hAlign !== 'left') ? `text-align: ${hAlign}` : '',
      (vAlign && vAlign !== 'middle') ? `vertical-align: ${vAlign}` : ''
    ]

    return ['tr', { 
      ...rest, 
      'data-row-height': rowHeight, 
      'data-row-style': rowStyle, 
      'data-text-align': hAlign, 
      'data-v-align': vAlign, 
      style: styles.filter(Boolean).join('; ') || undefined 
    }, 0]
  }
});

export const CustomTableCell = TableCell.extend({
  name: 'table_cell',
  addAttributes() {
    return {
      ...this.parent?.(),
      style: { default: null, parseHTML: el => el.getAttribute('style') },
      colWidth: { default: null, parseHTML: el => el.style.width || el.getAttribute('data-col-width') || null },
      colStyle: { default: null, parseHTML: el => el.getAttribute('data-col-style') || null },
      cellStyle: { default: null, parseHTML: el => el.getAttribute('data-cell-style') || null },
      hAlign: { default: null, parseHTML: el => el.style.textAlign || el.getAttribute('data-text-align') || null },
      vAlign: { default: null, parseHTML: el => el.style.verticalAlign || el.getAttribute('data-v-align') || null },
    }
  },
  renderHTML({ HTMLAttributes }) {
    const { style, hAlign, vAlign, colStyle, cellStyle, colWidth, ...rest } = HTMLAttributes;

    const styles = [
        style,
        (colWidth && String(colWidth) !== 'null') ? `width: ${colWidth}` : null,
        (hAlign && String(hAlign) !== 'null') ? `text-align: ${hAlign}` : null,
        (vAlign && String(vAlign) !== 'null') ? `vertical-align: ${vAlign}` : null,
    ].filter(Boolean).join('; ');

    return ['td', { 
        ...rest, 
        'data-col-width': colWidth || undefined,
        'data-col-style': colStyle || undefined, 
        'data-cell-style': cellStyle || undefined, 
        'data-text-align': hAlign || undefined, 
        'data-v-align': vAlign || undefined, 
        style: styles || undefined 
    }, 0];
  }
});

export const CustomTableHeader = TableHeader.extend({
  name: 'table_header',
  addAttributes: CustomTableCell.config.addAttributes,
  renderHTML({ HTMLAttributes }) {
    const { style, hAlign, vAlign, colStyle, cellStyle, colWidth, ...rest } = HTMLAttributes;

    const styles = [
        style,
        (colWidth && String(colWidth) !== 'null') ? `width: ${colWidth}` : null,
        (hAlign && String(hAlign) !== 'null') ? `text-align: ${hAlign}` : null,
        (vAlign && String(vAlign) !== 'null') ? `vertical-align: ${vAlign}` : null,
    ].filter(Boolean).join('; ');

    return ['th', {
        ...rest, 
        'data-col-width': colWidth || undefined,
        'data-col-style': colStyle || undefined, 
        'data-cell-style': cellStyle || undefined, 
        'data-text-align': hAlign || undefined, 
        'data-v-align': vAlign || undefined, 
        style: styles || undefined 
    }, 0];
  }
});

//
// 3. COMMAND TO SET CAPTION — NO DEPENDENCIES
//
export const TableCaption = Node.create({
  name: 'tableCaption',
  group: 'block',
  content: 'inline*',
  selectable: false, // Prevents users from clicking it directly
  draggable: false,
  parseHTML() { return [{ tag: 'caption' }] },
  renderHTML({ HTMLAttributes }) { return ['caption', HTMLAttributes, 0] },
});
